import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MapView from '../components/MapView';
import AddressSearch from '../components/AddressSearch';

/**
 * Quick Look page — a standalone, address-keyed map workspace.
 *
 * Quick Look is intentionally INDEPENDENT from Projects (per product spec):
 *   - Storage lives in the `quick_look_entries` Mongo collection, scoped
 *     by (user_id, place_id). Nothing in `projects` is read or written.
 *   - Users can draw, edit and delete measurements at any address; saves
 *     are permanent and per-user, surviving "Clear history" on the
 *     Recently Viewed dropdown.
 *   - Recently Viewed history (the dropdown when the search box is focused
 *     with empty query) is purely a navigation convenience and can be
 *     cleared without affecting any saved Quick Look entries.
 *
 * Save handlers below mirror ProjectPage's pattern so the same MapView
 * component can be reused with editing turned on — the only difference is
 * the API URLs (`/quick-look/...` instead of `/projects/...`).
 */
export default function QuickLookPage() {
  const { apiCall } = useAuth();
  const [address, setAddress] = useState(null);    // { lat, lng, address, place_id }
  const [entry, setEntry] = useState(null);         // currently loaded quick_look_entries row
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);

  // -------------------------------------------------------------------------
  // History (recently viewed) — dropdown population
  // -------------------------------------------------------------------------
  const handleClearRecent = useCallback(async () => {
    try {
      await apiCall('delete', '/auth/quick-look-history');
      setRecent([]);
    } catch (e) {
      console.error('Failed to clear quick-look history:', e);
    }
  }, [apiCall]);

  // -------------------------------------------------------------------------
  // Load entry helpers
  // -------------------------------------------------------------------------
  const loadEntryForPlace = useCallback(async (sel) => {
    setLoading(true);
    try {
      const res = await apiCall('get', `/quick-look/by-place?place_id=${encodeURIComponent(sel.place_id)}`);
      if (res.data) {
        setEntry(res.data);
      } else {
        // No entry yet — seed one server-side so the first measurement save
        // has somewhere to land. The lat/lng on map_data is the immutable
        // address-pin coordinates (location can drift with the viewport).
        const seed = await apiCall('put', `/quick-look/by-place?place_id=${encodeURIComponent(sel.place_id)}`, {
          address: sel.address,
          location: { lat: sel.lat, lng: sel.lng, zoom: 19 },
          map_data: { address: sel.address, place_id: sel.place_id, lat: sel.lat, lng: sel.lng },
        });
        setEntry(seed.data);
      }
    } catch (e) {
      console.error('Failed to load Quick Look entry:', e);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // -------------------------------------------------------------------------
  // Address picker handler
  // -------------------------------------------------------------------------
  const handleAddressPick = useCallback(async (sel) => {
    if (!sel?.place_id || sel.lat == null || sel.lng == null) return;
    setAddress(sel);
    setEntry(null);
    await loadEntryForPlace(sel);
    // Push to Recently Viewed (fire-and-forget; UI shouldn't block on persistence).
    apiCall('put', '/auth/last-quick-look', {
      place_id: sel.place_id,
      address: sel.address,
      lat: sel.lat,
      lng: sel.lng,
    }).catch(() => {});
    setRecent((prev) => {
      const newEntry = { place_id: sel.place_id, address: sel.address, lat: sel.lat, lng: sel.lng };
      const without = (prev || []).filter((r) => r.place_id !== sel.place_id);
      return [newEntry, ...without].slice(0, 10);
    });
  }, [apiCall, loadEntryForPlace]);

  // -------------------------------------------------------------------------
  // Hydrate from history on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const histRes = await apiCall('get', '/auth/quick-look-history');
        const hist = Array.isArray(histRes.data) ? histRes.data : [];
        if (cancelled) return;
        setRecent(hist);
        const last = hist[0];
        if (!last || !last.place_id) return;
        setAddress({
          place_id: last.place_id,
          address: last.address,
          lat: last.lat,
          lng: last.lng,
        });
        await loadEntryForPlace(last);
      } catch {
        // Empty history is fine — leave the page in its blank state.
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall, loadEntryForPlace]);

  // -------------------------------------------------------------------------
  // MapView CRUD handlers — same shape as ProjectPage but targeting
  // /quick-look/* endpoints so nothing in `projects` is ever mutated.
  // -------------------------------------------------------------------------
  const handleSaveMeasurement = useCallback(async (measurement) => {
    if (!address?.place_id) return measurement;
    try {
      const res = await apiCall('post', '/quick-look/measurements', {
        place_id: address.place_id,
        address: address.address,
        location: { lat: address.lat, lng: address.lng, zoom: 19 },
        ...measurement,
      });
      return res.data;
    } catch (e) {
      console.error('Failed to save quick-look measurement:', e);
      return measurement;
    }
  }, [apiCall, address]);

  const handleDeleteMeasurement = useCallback(async (measurement) => {
    if (!address?.place_id || !measurement?.id) return;
    try {
      await apiCall('delete', `/quick-look/${address.place_id}/measurements/${measurement.id}`);
    } catch (e) {
      console.error('Failed to delete quick-look measurement:', e);
    }
  }, [apiCall, address]);

  const handleUpdateMeasurement = useCallback(async (measurementId, updates) => {
    if (!address?.place_id || !measurementId) return;
    try {
      await apiCall('patch', `/quick-look/${address.place_id}/measurements/${measurementId}`, updates);
    } catch (e) {
      console.error('Failed to update quick-look measurement:', e);
    }
  }, [apiCall, address]);

  const handleSaveLocation = useCallback(async (location) => {
    if (!address?.place_id) return;
    try {
      await apiCall('put', `/quick-look/by-place?place_id=${encodeURIComponent(address.place_id)}`, { location });
    } catch (e) {
      console.error('Failed to save quick-look location:', e);
    }
  }, [apiCall, address]);

  // The "project" prop MapView consumes — we synthesise from the loaded
  // quick_look_entries row. Falls back to a minimal stub built from the
  // raw address so the map still renders while the entry is being seeded.
  const projectShape = useMemo(() => {
    if (entry) {
      // Build map_data carrying immutable address coords. If the persisted
      // entry doesn't have them (older row before this fix), fall back to
      // the currently-typed address; otherwise keep what's stored.
      const md = entry.map_data || {};
      const hasAddrCoords = typeof md.lat === 'number' && typeof md.lng === 'number';
      const enrichedMapData = hasAddrCoords ? md : {
        ...md,
        address: md.address || entry.address,
        place_id: md.place_id || entry.place_id,
        lat: address?.lat ?? entry.location?.lat,
        lng: address?.lng ?? entry.location?.lng,
      };
      return {
        id: entry.id || `__ql__${entry.place_id}`,
        name: entry.address || 'Quick Look',
        location: entry.location || { lat: address?.lat, lng: address?.lng, zoom: 19 },
        map_data: enrichedMapData,
        measurements: entry.measurements || [],
        boundary_preferences: entry.boundary_preferences || {},
        imagery_date: entry.imagery_date || null,
      };
    }
    if (address) {
      return {
        id: `__ql_stub__${address.place_id}`,
        name: address.address,
        location: { lat: address.lat, lng: address.lng, zoom: 19 },
        map_data: {
          address: address.address,
          place_id: address.place_id,
          lat: address.lat,
          lng: address.lng,
        },
        measurements: [],
        boundary_preferences: {},
      };
    }
    return null;
  }, [entry, address]);

  return (
    <div className="h-full flex flex-col" data-testid="quick-look-page">
      {/* Top search bar — z-30 + relative ensure the dropdown sits above
          the Google Maps canvas so clicks reach the option buttons. */}
      <div className="border-b border-border bg-card/95 backdrop-blur-sm pl-1 pr-4 py-3 flex items-center gap-3 flex-wrap relative z-30">
        <div className="w-[18rem] shrink-0">
          <AddressSearch
            onSelect={handleAddressPick}
            placeholder="Search an address"
            testId="quick-look-search"
            recentItems={recent}
            onClearRecent={handleClearRecent}
          />
        </div>

        {/* Currently-loaded address pill. We show it as soon as we have an
            address, even before the entry hydrates, so the user always
            knows which property they're looking at. Click the × to clear
            the current selection and return to the empty search state. */}
        {address?.address && (
          <div
            className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium max-w-[60%]"
            title={address.address}
            data-testid="quick-look-current-address"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="shrink-0">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z" />
            </svg>
            <span className="truncate">{address.address}</span>
            <button
              type="button"
              onClick={() => {
                setAddress(null);
                setEntry(null);
              }}
              className="ml-1 w-5 h-5 rounded-full inline-flex items-center justify-center hover:bg-primary/20 transition-colors"
              title="Clear current address"
              data-testid="quick-look-clear-address"
              aria-label="Clear current address"
            >
              <svg width="9" height="9" viewBox="0 0 20 20" fill="currentColor"><path d="M10 8.586l4.293-4.293 1.414 1.414L11.414 10l4.293 4.293-1.414 1.414L10 11.414l-4.293 4.293-1.414-1.414L8.586 10 4.293 5.707l1.414-1.414L10 8.586z"/></svg>
            </button>
          </div>
        )}

        {loading && (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
            <span className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            Loading…
          </span>
        )}
      </div>

      <div className="flex-1 relative">
        {projectShape ? (
          <MapView
            key={projectShape.id}
            project={projectShape}
            projectApiPath={address?.place_id ? `/quick-look/by-place?place_id=${encodeURIComponent(address.place_id)}` : null}
            onSaveMeasurement={handleSaveMeasurement}
            onDeleteMeasurement={handleDeleteMeasurement}
            onUpdateMeasurement={handleUpdateMeasurement}
            onSaveLocation={handleSaveLocation}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/20" data-testid="quick-look-empty">
            <div className="text-center max-w-md p-6">
              <h2 className="text-xl font-bold mb-2">Look up a property</h2>
              <p className="text-sm text-muted-foreground">
                Type any address above. Anything you draw here is saved against the address — no project required.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
